import React, { useEffect, useId, useState } from "react";
import {
  HandDriveModel,
  rescueSheetApi,
  RescueSheetLicenseModel,
} from "../api/rescueSheetApi";
import ErrorDisplay from "../common/ErrorDisplay";
import { Problem } from "../api/backendApi";
import {
  Button,
  ButtonGroup,
  ButtonToolbar,
  Form,
  InputGroup,
  ToggleButton,
  ToggleButtonGroup,
} from "react-bootstrap";
import { LocaleModel, Locales } from "../api/drawingApi";
import { Fullscreen, FiletypeSvg } from "react-bootstrap-icons";

interface RescueSheetViewerProps {
  vehicleFileNumber: string;
  componentString?: string;
  refreshSwitch: number;
  recId?: number;
  onPopupClick?: () => void;
  useDrawing: boolean;
  onComponentClick?: (componentName: string, refCount: number) => void;
  onAttributeWindowClick?: (attributeWindowName: string) => void;
  onPdfClick?: (pdfId: string, locale: LocaleModel) => void;
}

enum Orientation {
  Portrait = 1,
  Landscape = 2,
  SmallLandscape = 3,
}

function RescueSheetViewer({
  vehicleFileNumber,
  componentString: initialComponentString,
  recId: initialRecId,
  refreshSwitch,
  onPopupClick,
  useDrawing,
  onComponentClick,
  onAttributeWindowClick,
  onPdfClick,
}: RescueSheetViewerProps) {
  const [svgUrl, setSvgUrl] = useState<string>();
  const [error, setError] = useState<Problem>();
  const [refreshFlag, setRefreshflag] = useState<boolean>(false);
  const [locale, setLocale] = useState<LocaleModel>(LocaleModel.English);
  const [handDrive, setHandDrive] = useState<HandDriveModel>();
  const [license, setLicense] = useState<RescueSheetLicenseModel>(
    RescueSheetLicenseModel.RDW,
  );
  const [darkMode, setDarkMode] = useState(false);
  const [recId, setRecId] = useState(initialRecId);
  const [componentString, setComponentString] = useState(
    initialComponentString,
  );
  const [orientation, setOrientation] = useState(Orientation.SmallLandscape);
  const controlId = useId();

  useEffect(() => {
    const handleEvent = (event: MessageEvent) => {
      if (
        event.origin === "null" &&
        (typeof event.data === "string" || event.data instanceof String)
      ) {
        // came from the iframe
        if (event.data.startsWith(controlId + "SHEET: ")) {
          const tokens = event.data.split(":").map((t) => t.trim());
          const lastToken = tokens[tokens.length - 1];

          if (lastToken.startsWith("grp-AW-")) {
            if (lastToken.indexOf("-sym-") >= 0) {
              // component
              const matches = lastToken.match("-ref-(\\d+)-sym-(.*)$");

              matches?.length === 3 &&
                onComponentClick &&
                onComponentClick(matches[2], Number.parseInt(matches[1], 10));
            } else {
              // informational attribute window
              onAttributeWindowClick &&
                onAttributeWindowClick(lastToken.substring("grp-AW-".length));
            }
          }
        } else if (event.data.startsWith(controlId + "SHEETPDF: ")) {
          onPdfClick &&
            onPdfClick(
              event.data.substring((controlId + "SHEETPDF: ").length),
              locale,
            );
        }
      }
    };

    window.addEventListener("message", handleEvent, false);
    return function cleanup() {
      window.removeEventListener("message", handleEvent);
    };
  }, [onAttributeWindowClick, onComponentClick, onPdfClick, locale, controlId]);

  useEffect(() => {
    setRecId(initialRecId);
    setComponentString(initialComponentString);
    setHandDrive(undefined);
    setRefreshflag(!refreshFlag);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRecId, initialComponentString, refreshSwitch]);

  useEffect(() => {
    async function refreshRescueSheet() {
      let width: number;
      let height: number;

      switch (orientation) {
        case Orientation.Landscape:
          width = 1276;
          height = 742;
          break;

        case Orientation.Portrait:
          width = 844;
          height = 742;
          break;

        default:
        case Orientation.SmallLandscape:
          width = 987;
          height = 678;
          break;
      }

      const result = await rescueSheetApi.getClassicRescueSheet(
        vehicleFileNumber,
        useDrawing,
        recId,
        componentString,
        locale,
        handDrive,
        license,
        width,
        height,
        darkMode,
        window.origin,
        controlId,
      );

      if (result.ok) {
        setError(undefined);

        const blob = new Blob([result.value], { type: "image/svg+xml" });

        setSvgUrl(URL.createObjectURL(blob));
      } else {
        setError(result.problem);
        setSvgUrl(undefined);
      }
    }

    refreshRescueSheet();
  }, [
    componentString,
    darkMode,
    handDrive,
    license,
    locale,
    orientation,
    recId,
    refreshFlag,
    vehicleFileNumber,
    useDrawing,
    controlId,
  ]);

  const handleLocaleChange = (locale: LocaleModel) => {
    setLocale(locale);
    setRefreshflag(!refreshFlag);
  };

  const handleLicenseClick = (license: RescueSheetLicenseModel) => {
    setLicense(license);
    setRefreshflag(!refreshFlag);
  };

  const handleOrientationClick = (orientation: Orientation) => {
    setOrientation(orientation);
    setRefreshflag(!refreshFlag);
  };

  const handleHandDriveClick = (clickedHandDrive: HandDriveModel) => {
    setHandDrive(handDrive !== clickedHandDrive ? clickedHandDrive : undefined);
    setRefreshflag(!refreshFlag);
  };

  const handleResetClick = () => {
    setLocale(LocaleModel.English);
    setHandDrive(undefined);
    setRefreshflag(!refreshFlag);
    setComponentString(undefined);
    setRecId(undefined);
  };

  const handleDarkModeClick = (darkMode: boolean) => {
    setDarkMode(darkMode);
    setRefreshflag(!refreshFlag);
  };

  const downloadSvg = () => {
    if (!svgUrl) return;
    const anchorElement = document.createElement("A") as HTMLAnchorElement;
    anchorElement.download = vehicleFileNumber + ".svg";
    anchorElement.href = svgUrl;
    anchorElement.click();
  };

  return (
    <div className="h-100" style={{ display: "flex", flexDirection: "column" }}>
      <ButtonToolbar className="mb-3">
        <ButtonGroup className="me-2" size="sm">
          <Button onClick={() => handleResetClick()}>Reload</Button>
        </ButtonGroup>
        <InputGroup className="me-2">
          <Form.Select
            size="sm"
            value={locale}
            onChange={(e) =>
              handleLocaleChange(Number(e.target.value) as LocaleModel)
            }
          >
            {Locales.map((l) => (
              <option key={l} value={l}>
                {LocaleModel[l]}
              </option>
            ))}
          </Form.Select>
        </InputGroup>
        <div className="btn-group me-2" role="group">
          <input
            type="checkbox"
            className="btn-check"
            id={controlId + "_btn-check-LHD"}
            checked={handDrive === HandDriveModel.LeftHandDrive}
            autoComplete="off"
            onChange={(e) => handleHandDriveClick(HandDriveModel.LeftHandDrive)}
            disabled={(componentString?.length ?? 0) > 0}
          />
          <label
            className="btn btn-primary btn-sm"
            htmlFor={controlId + "_btn-check-LHD"}
          >
            LHD
          </label>

          <input
            type="checkbox"
            className="btn-check"
            id={controlId + "_btn-check-RHD"}
            checked={handDrive === HandDriveModel.RightHandDrive}
            autoComplete="off"
            onChange={(e) =>
              handleHandDriveClick(HandDriveModel.RightHandDrive)
            }
            disabled={(componentString?.length ?? 0) > 0}
          />
          <label
            className="btn btn-primary btn-sm"
            htmlFor={controlId + "_btn-check-RHD"}
          >
            RHD
          </label>
        </div>
        <ToggleButtonGroup
          value={license}
          name={controlId + "_license"}
          type="radio"
          onChange={(e) => handleLicenseClick(e)}
          className="me-2"
        >
          <ToggleButton
            id={controlId + "_license_1"}
            value={RescueSheetLicenseModel.Lite}
            size="sm"
          >
            Lite
          </ToggleButton>
          <ToggleButton
            id={controlId + "_license_2"}
            value={RescueSheetLicenseModel.RDW}
            size="sm"
          >
            RDW
          </ToggleButton>
          <ToggleButton
            id={controlId + "_license_3"}
            value={RescueSheetLicenseModel.RSA}
            size="sm"
          >
            RSA
          </ToggleButton>
        </ToggleButtonGroup>

        <input
          type="checkbox"
          className="btn-check"
          id={controlId + "_btn-check"}
          autoComplete="off"
          checked={darkMode}
          onChange={(e) => handleDarkModeClick(e.target.checked)}
        />
        <label
          className="btn btn-primary btn-sm me-2"
          htmlFor={controlId + "_btn-check"}
        >
          Dark Mode
        </label>

        <ToggleButtonGroup
          value={orientation}
          name={controlId + "_orientation"}
          onChange={(e) => handleOrientationClick(e)}
          className="me-2"
        >
          <ToggleButton
            value={Orientation.Portrait}
            id={controlId + "_orientation_portrait"}
            size="sm"
          >
            Portrait
          </ToggleButton>
          <ToggleButton
            value={Orientation.Landscape}
            id={controlId + "_orientation_landscape"}
            size="sm"
          >
            Landscape
          </ToggleButton>
          <ToggleButton
            value={Orientation.SmallLandscape}
            id={controlId + "_orientation_small_landscape"}
            size="sm"
          >
            Small Landscape
          </ToggleButton>
        </ToggleButtonGroup>
        {onPopupClick && (
          <Button
            title="Show in popup"
            onClick={() => onPopupClick()}
            size="sm"
            variant="info"
            className="me-2"
          >
            <Fullscreen size={16} style={{ verticalAlign: "sub" }} />
          </Button>
        )}
        {svgUrl && (
          <Button
            title="Download .svg"
            size="sm"
            onClick={() => downloadSvg()}
            className="me-2"
          >
            <FiletypeSvg style={{ display: "block" }} />
          </Button>
        )}
      </ButtonToolbar>
      <ErrorDisplay error={error} />
      {svgUrl && (
        <iframe
          sandbox="allow-scripts"
          className="h-100"
          style={{}}
          title="rescue sheet viewer"
          src={svgUrl}
        ></iframe>
      )}
    </div>
  );
}

export default RescueSheetViewer;
